/* ──────────────────────────────────────────────────────────────
   Setup/Tags/index.tsx · Tags flat list (live — sk-catalog-api /tags)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import {
  Button,
  Input,
  Modal,
  Empty,
  ConfirmDialog,
  Popover,
  MenuItem,
  useToast,
} from '../../components/ui';
import { getTags, createTag, updateTag, deleteTag } from '../../services/catalog/taxonomy';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { Tag } from '../../schemas/catalog/taxonomy';

// ── TagsView (main export) ────────────────────────────────────

export default function TagsView() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const toast = useToast();

  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [delTarget, setDelTarget] = useState<Tag | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState<Tag | null>(null);
  const [editName, setEditName] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTags();
      setTags(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await createTag({ name: name.trim() });
      showSuccess(res, 'Tag created', `“${name.trim()}” added.`);
      setName('');
      setAddOpen(false);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not create tag');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (t: Tag) => { setEditTarget(t); setEditName(t.name); };

  const onEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    setEditing(true);
    try {
      await updateTag(editTarget.id, { name: editName.trim() });
      showSuccess({}, 'Tag updated', `Renamed to “${editName.trim()}”.`);
      setEditTarget(null);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not update tag');
    } finally {
      setEditing(false);
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteTag(delTarget.id);
      showSuccess({}, 'Tag removed', `“${delTarget.name}” deleted.`);
      setTags(prev => prev.filter(x => x.id !== delTarget.id));
      setDelTarget(null);
    } catch (err) {
      handleApiError(err, 'Could not remove tag');
    } finally {
      setDeleting(false);
    }
  };

  const sectionCardStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    ...(shadows.sm as ViewStyle),
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Tags
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            Flat labels for filtering &amp; discovery.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={() => setAddOpen(true)}>Add tag</Button>
      </View>

      <View style={[sectionCardStyle, { maxWidth: 560 }]}>
        <View style={{ padding: 16 }}>
          {loading ? (
            <View style={{ paddingVertical: 32, alignItems: 'center' }}>
              <ActivityIndicator color={colors.accent} />
            </View>
          ) : tags.length === 0 ? (
            <Empty title="No tags yet">Add your first tag to start labelling items.</Empty>
          ) : (
            <View style={{ gap: 8 }}>
              {tags.map(t => (
                // List row: name on the left, ⋮ menu on the right.
                <View
                  key={t.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: 16,
                    paddingRight: 6,
                    paddingVertical: 7,
                    borderWidth: 1,
                    borderColor: colors.line,
                    borderRadius: radii.r2,
                    backgroundColor: colors.card,
                  }}
                >
                  <Text style={{ fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans, fontWeight: fontWeight.medium }}>
                    {t.name}
                  </Text>
                  <Popover
                    align="right"
                    width={150}
                    trigger={<Button kind="ghost" size="xs" iconOnly icon="more" aria-label={`Actions for ${t.name}`} />}
                  >
                    {(close: () => void) => (
                      <>
                        <MenuItem icon="edit" onPress={() => { close(); openEdit(t); }}>Edit</MenuItem>
                        <MenuItem icon="trash" danger onPress={() => { close(); setDelTarget(t); }}>Delete</MenuItem>
                      </>
                    )}
                  </Popover>
                </View>
              ))}
            </View>
          )}
        </View>
      </View>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        size="sm"
        title="Add a tag"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOpen(false)}>Cancel</Button>
            <Button kind="accent" icon="plus" disabled={!name.trim() || busy} onPress={onAdd}>
              {busy ? 'Adding…' : 'Add tag'}
            </Button>
          </View>
        }
      >
        <Input label="Tag name" placeholder="e.g. spicy" value={name} onChangeText={setName} autoFocus />
      </Modal>

      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        size="sm"
        title="Edit tag"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditTarget(null)}>Cancel</Button>
            <Button kind="accent" icon="edit" disabled={!editName.trim() || editing} onPress={onEdit}>
              {editing ? 'Saving…' : 'Save'}
            </Button>
          </View>
        }
      >
        <Input label="Tag name" placeholder="e.g. spicy" value={editName} onChangeText={setEditName} autoFocus />
      </Modal>

      <ConfirmDialog
        visible={!!delTarget}
        title="Delete tag?"
        message={delTarget ? `"${delTarget.name}" will be removed and unassigned from all items.` : ''}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
